import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';
import { useCredits } from '@/contexts/CreditsContext';
import { functionsService } from '@/services/functionsService';

interface SubscriptionTier {
  id: string;
  name: string;
  price: number;
  credits: number;
  features: string[];
}

export const Subscription: React.FC = () => {
  const { user } = useAuth();
  const { balance, tier, refreshBalance } = useCredits();
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    const success = searchParams.get('success');
    if (success === '1') {
      setSuccessMessage('Thank you! Your subscription is active.');
      refreshBalance();
      window.history.replaceState({}, '', '/subscription');
    }
  }, [searchParams, refreshBalance]);

  const tiers: SubscriptionTier[] = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      credits: 50,
      features: [
        '50 credits per month',
        'Basic transcript analysis',
        'Basic filing analysis',
        'Limited chat queries',
      ],
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 29,
      credits: 500,
      features: [
        '500 credits per month',
        'Advanced transcript analysis',
        'Advanced filing analysis',
        'Unlimited chat queries',
        'Priority support',
        'Export to PDF',
      ],
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 99,
      credits: 2000,
      features: [
        '2000 credits per month',
        'All Pro features',
        'API access',
        'Custom integrations',
        'Dedicated support',
        'Advanced analytics',
      ],
    },
  ];

  const handleUpgrade = async (tierId: string) => {
    if (!user) {
      alert('Please sign in to upgrade your subscription');
      return;
    }
    if (tierId === 'free') return;
    // Backend expects "premium" | "enterprise"
    const planId = tierId === 'pro' ? 'premium' : tierId;

    setLoading(true);
    try {
      const base = window.location.origin;
      const { url } = await functionsService.createCheckoutSession(
        planId,
        `${base}/subscription?success=1&session_id={CHECKOUT_SESSION_ID}`,
        `${base}/subscription?canceled=1`
      );
      if (url) {
        window.location.href = url;
        return;
      }
      throw new Error('No checkout URL returned');
    } catch (error: any) {
      console.error('Failed to upgrade:', error);
      alert(error?.message || 'Failed to start checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="mb-8">
          {successMessage && (
            <div className="mb-6 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-green-800">
              {successMessage}
            </div>
          )}
          <h1 className="h1 text-4xl mb-2">Subscription Management</h1>
          <p className="body-text text-gray-600">
            Manage your subscription and billing information
          </p>
        </div>

        {/* Current Plan */}
        <Card className="mb-8">
          <h2 className="sub-header text-2xl mb-4">Current Plan</h2>
          <div className="flex items-center justify-between">
            <div>
              <p className="body-text text-lg font-medium capitalize mb-1">
                {tier || 'Free'} Plan
              </p>
              <p className="body-text text-gray-600">
                Available Credits: <span className="font-medium text-blazerex-green">{balance}</span>
              </p>
            </div>
            <div className="text-right">
              <p className="body-text text-sm text-gray-500">Monthly Billing</p>
              <p className="sub-header text-xl">
                ${tiers.find(t => t.id === tier)?.price || 0}/month
              </p>
            </div>
          </div>
        </Card>

        {/* Available Plans */}
        <div className="mb-8">
          <h2 className="sub-header text-2xl mb-6">Available Plans</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {tiers.map((plan) => (
              <Card
                key={plan.id}
                className={`relative ${
                  plan.id === tier ? 'ring-2 ring-blazerex-green' : ''
                }`}
              >
                {plan.id === tier && (
                  <div className="absolute top-4 right-4 bg-blazerex-green text-white px-3 py-1 rounded-full text-xs font-medium">
                    Current
                  </div>
                )}
                <div className="mb-6">
                  <h3 className="sub-header text-2xl mb-2">{plan.name}</h3>
                  <div className="mb-4">
                    <span className="h1 text-4xl">${plan.price}</span>
                    <span className="body-text text-gray-600">/month</span>
                  </div>
                  <p className="body-text text-gray-600 mb-4">
                    {plan.credits} credits per month
                  </p>
                </div>
                <ul className="space-y-2 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="body-text text-gray-700 flex items-start gap-2">
                      <svg
                        className="w-5 h-5 text-blazerex-green mt-0.5 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  variant={plan.id === tier ? 'outline' : 'primary'}
                  className="w-full"
                  onClick={() => handleUpgrade(plan.id)}
                  disabled={plan.id === tier || loading}
                >
                  {plan.id === tier ? 'Current Plan' : plan.price === 0 ? 'Downgrade' : 'Upgrade'}
                </Button>
              </Card>
            ))}
          </div>
        </div>

        {/* Billing History */}
        <Card>
          <h2 className="sub-header text-2xl mb-4">Billing History</h2>
          <div className="text-center py-8">
            <p className="body-text text-gray-500">
              No billing history available yet. Upgrade above to start a subscription.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

